import React from 'react';
import { Loader2, CreditCard, Receipt, History } from 'lucide-react';

// Generic loading spinner component
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <Loader2 
      className={`animate-spin ${sizeClasses[size]} ${className}`} 
    />
  );
};

// Inline loading spinner for buttons
export const ButtonLoadingSpinner = ({ text = 'Loading...', className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <LoadingSpinner size="sm" className="text-current" />
    <span>{text}</span>
  </div>
);

// Full page loading overlay
export const LoadingOverlay = ({ message = 'Loading...', isVisible = true }) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg p-6 shadow-xl">
        <div className="flex items-center gap-3">
          <LoadingSpinner size="lg" className="text-blue-600 dark:text-blue-300" />
          <span className="text-lg font-medium text-content">{message}</span>
        </div>
      </div>
    </div>
  );
};

// Payment method loading skeleton
export const PaymentMethodSkeleton = () => (
  <div className="p-4 border border-line rounded-lg bg-gradient-to-br from-gray-50 to-white animate-pulse">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-gray-300 rounded"></div>
        <div className="w-16 h-4 bg-gray-300 rounded"></div>
      </div>
      <div className="w-12 h-5 bg-gray-300 rounded-full"></div>
    </div>
    <div className="mb-3">
      <div className="w-32 h-5 bg-gray-300 rounded mb-1"></div>
      <div className="w-20 h-4 bg-gray-300 rounded"></div>
    </div>
    <div className="flex items-center justify-end gap-1">
      <div className="w-8 h-8 bg-gray-300 rounded-md"></div>
      <div className="w-8 h-8 bg-gray-300 rounded-md"></div>
    </div>
  </div>
);

// Transaction loading skeleton
export const TransactionSkeleton = () => (
  <div className="p-4 border-b border-line animate-pulse">
    <div className="flex justify-between items-start mb-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="w-24 h-4 bg-gray-300 rounded"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="w-16 h-5 bg-gray-300 rounded-full"></div>
      </div>
    </div>
    <div className="mb-2">
      <div className="w-48 h-4 bg-gray-300 rounded mb-1"></div>
      <div className="w-20 h-3 bg-gray-300 rounded"></div>
    </div>
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="w-20 h-4 bg-gray-300 rounded"></div>
      </div>
      <div className="w-16 h-4 bg-gray-300 rounded"></div>
    </div>
  </div>
);

// Table loading skeleton
export const TableLoadingSkeleton = ({ rows = 5, columns = 6 }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 dark:bg-dark-700">
        <tr>
          {Array.from({ length: columns }).map((_, index) => (
            <th key={index} className="px-6 py-3">
              <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-surface divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                <div className="w-full h-4 bg-gray-300 rounded animate-pulse"></div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Section loading component with icon
export const SectionLoading = ({ 
  icon: Icon = CreditCard, 
  title = 'Loading...', 
  description = 'Please wait while we load your data' 
}) => (
  <div className="bg-surface rounded-xl shadow-lg border border-line-subtle">
    <div className="p-6 border-b border-line">
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-content-subtle" />
        <div>
          <h3 className="text-lg font-semibold text-content">{title}</h3>
          <p className="text-sm text-content-secondary">{description}</p>
        </div>
      </div>
    </div>
    <div className="p-6 flex items-center justify-center">
      <div className="flex items-center gap-3">
        <LoadingSpinner size="lg" className="text-blue-600 dark:text-blue-300" />
        <span className="text-content-secondary">Loading data...</span>
      </div>
    </div>
  </div>
);

// Payment methods section loading
export const PaymentMethodsLoading = () => (
  <SectionLoading 
    icon={CreditCard}
    title="Payment Methods"
    description="Loading your payment methods..."
  />
);

// Billing history section loading
export const BillingHistoryLoading = () => (
  <SectionLoading 
    icon={Receipt}
    title="Billing History"
    description="Loading your billing history..."
  />
);

// Transaction history section loading
export const TransactionHistoryLoading = () => (
  <SectionLoading 
    icon={History}
    title="Transaction History"
    description="Loading your transaction history..."
  />
);

// Form loading state
export const FormLoading = ({ message = 'Processing...' }) => (
  <div className="flex items-center justify-center p-4">
    <div className="flex items-center gap-3">
      <LoadingSpinner size="md" className="text-blue-600 dark:text-blue-300" />
      <span className="text-gray-700 dark:text-gray-300">{message}</span>
    </div>
  </div>
);

// Modal loading state
export const ModalLoading = ({ message = 'Loading...' }) => (
  <div className="p-6 flex items-center justify-center">
    <div className="flex items-center gap-3">
      <LoadingSpinner size="lg" className="text-blue-600 dark:text-blue-300" />
      <span className="text-lg text-gray-700 dark:text-gray-300">{message}</span>
    </div>
  </div>
);

// Ticket list loading state
export const TicketListLoading = ({ count = 5, message = 'Loading tickets...' }) => (
  <div className="space-y-4">
    {message && (
      <div className="flex items-center justify-center py-4">
        <div className="flex items-center gap-3">
          <LoadingSpinner size="md" className="text-blue-600 dark:text-blue-300" />
          <span className="text-content-secondary">{message}</span>
        </div>
      </div>
    )}
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="bg-surface border border-line rounded-lg p-4 animate-pulse">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
            <div>
              <div className="w-32 h-4 bg-gray-300 rounded mb-1"></div>
              <div className="w-24 h-3 bg-gray-300 rounded"></div>
            </div>
          </div>
          <div className="w-16 h-5 bg-gray-300 rounded-full"></div>
        </div>
        <div className="w-full h-4 bg-gray-300 rounded mb-2"></div>
        <div className="w-3/4 h-4 bg-gray-300 rounded"></div>
      </div>
    ))}
  </div>
);

// Empty state component
export const EmptyTicketState = ({ 
  title = 'No tickets found', 
  description = 'There are no tickets to display at this time.',
  icon: Icon = null,
  userRole = 'customer',
  hasFilters = false,
  onClearFilters = null,
  onCreateTicket = null
}) => {
  // Customize message based on role and filters
  const getEmptyStateContent = () => {
    if (hasFilters) {
      return {
        title: 'No tickets match your filters',
        description: 'Try adjusting your search criteria or clearing filters to see more tickets.'
      };
    }
    
    switch (userRole) {
      case 'customer':
        return {
          title: 'No support tickets yet',
          description: 'You haven\'t created any support tickets. Click "New Ticket" to get started.'
        };
      case 'agent':
        return {
          title: 'No tickets to display',
          description: 'There are currently no tickets matching your view criteria.'
        };
      case 'manager':
        return {
          title: 'No tickets found',
          description: 'There are no tickets to display in the current view.'
        };
      default:
        return { title, description };
    }
  };

  const content = getEmptyStateContent();

  return (
    <div className="text-center py-12">
      {Icon && <Icon className="w-12 h-12 text-content-subtle mx-auto mb-4" />}
      <h3 className="text-lg font-medium text-content mb-2">{content.title}</h3>
      <p className="text-content-secondary mb-6">{content.description}</p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {hasFilters && onClearFilters && (
          <button
            onClick={onClearFilters}
            className="px-4 py-2 border border-line-strong rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            Clear Filters
          </button>
        )}
        
        {userRole === 'customer' && onCreateTicket && (
          <button
            onClick={onCreateTicket}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create New Ticket
          </button>
        )}
      </div>
    </div>
  );
};

// Error state component
export const ErrorState = ({ 
  title = 'Something went wrong', 
  description = 'We encountered an error while loading your data.',
  error = null,
  onRetry = null,
  showRetry = true
}) => {
  // Use error message if provided
  const errorTitle = error?.title || title;
  const errorDescription = error?.message || error || description;

  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-6 h-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-content mb-2">{errorTitle}</h3>
      <p className="text-content-secondary mb-4">{errorDescription}</p>
      {onRetry && showRetry && (
        <button
          onClick={onRetry}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

// Button loading state
export const ButtonLoading = ({ text = 'Loading...', size = 'md' }) => {
  const sizeClasses = {
    sm: 'px-3 py-1 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };

  return (
    <div className={`inline-flex items-center gap-2 ${sizeClasses[size]} bg-gray-100 dark:bg-dark-700 text-content-secondary rounded-lg cursor-not-allowed`}>
      <LoadingSpinner size="sm" />
      <span>{text}</span>
    </div>
  );
};

// Metrics loading state
export const MetricsLoading = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="bg-surface p-6 rounded-lg border border-line animate-pulse">
        <div className="flex items-center justify-between mb-2">
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
        </div>
        <div className="w-16 h-8 bg-gray-300 rounded mb-1"></div>
        <div className="w-24 h-4 bg-gray-300 rounded"></div>
      </div>
    ))}
  </div>
);